import React, { useState, useEffect } from "react";

const SukanyaSamriddhiCalculator = () => {
  const [yearlyDeposit, setYearlyDeposit] = useState(150000);
  const [annualInterestRate, setAnnualInterestRate] = useState(8.2);
  const [girlCurrentAge, setGirlCurrentAge] = useState(5);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState({
    futureValue: "0.00",
    totalInvestment: "0.00",
    estimatedReturns: "0.00",
    inflationAdjustedValue: "0.00",
    depositYears: "0",
    maturityAge: "0",
  });

  // SSY Calculation Function
  const calculateSSY = () => {
    const P = parseFloat(yearlyDeposit);
    const r = parseFloat(annualInterestRate) / 100;
    const currentAge = parseFloat(girlCurrentAge);
    const inflation = parseFloat(inflationRate) / 100;

    if (isNaN(P) || isNaN(r) || isNaN(currentAge) || P <= 0 || currentAge < 0 || currentAge >= 10) return;

    // SSY: Deposits for 15 years, maturity at 21 years from account opening
    const depositYears = 15;
    const maturityAge = currentAge + 21;
    const totalYears = 21; // Account matures after 21 years

    // Calculate maturity amount with compound interest
    // For deposit years (1-15): Each deposit compounds for remaining years
    let maturityAmount = 0;
    for (let i = 1; i <= depositYears; i++) {
      const yearsToMaturity = totalYears - i + 1;
      maturityAmount += P * Math.pow(1 + r, yearsToMaturity);
    }
    
    const totalDeposit = P * depositYears;
    const interestEarned = maturityAmount - totalDeposit;
    
    // Calculate inflation-adjusted value
    const inflationAdjustedValue = maturityAmount / Math.pow(1 + inflation, totalYears);

    setResult({
      futureValue: maturityAmount.toFixed(2),
      totalInvestment: totalDeposit.toFixed(2),
      estimatedReturns: interestEarned.toFixed(2),
      inflationAdjustedValue: inflationAdjustedValue.toFixed(2),
      depositYears: depositYears.toString(),
      maturityAge: maturityAge.toString(),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
  calculateSSY();
}, [yearlyDeposit, annualInterestRate, girlCurrentAge, inflationRate]);


  const growthPercentage = result.totalInvestment > 0 
    ? ((Number(result.estimatedReturns) / Number(result.totalInvestment)) * 100).toFixed(1)
    : 0;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1>Sukanya Samriddhi Yojana Calculator</h1>
          <p>
            Calculate your girl child's future corpus with guaranteed returns and complete tax exemption
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                SSY Details
              </h2>

              {/* Yearly Deposit */}
              <div className="input-group">
                <label>Yearly Deposit</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={yearlyDeposit}
                    onChange={(e) => setYearlyDeposit(e.target.value)}
                    className="calc-input"
                    placeholder="150000"
                  />
                </div>
                <div className="quick-select-group">
                  {[25000, 50000, 100000, 150000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setYearlyDeposit(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Annual Interest Rate */}
              <div className="input-group">
                <label>Annual Interest Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={annualInterestRate}
                    onChange={(e) => setAnnualInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="8.2"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[7.6, 8, 8.2, 8.5].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setAnnualInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Girl's Current Age */}
              <div className="input-group">
                <label>Girl's Current Age</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={girlCurrentAge}
                    onChange={(e) => setGirlCurrentAge(e.target.value)}
                    className="calc-input"
                    placeholder="5"
                    min="0"
                    max="9"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 3, 5, 8].map((age) => (
                    <button
                      key={age}
                      onClick={() => setGirlCurrentAge(age)}
                      className="quick-select-btn success"
                    >
                      {age}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Expected Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculateSSY} className="calculate-btn">
                Calculate SSY Returns
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                SSY Summary
              </h2>

              <div className="result-card">
                {/* Total Investment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Investment</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInvestment).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Over {result.depositYears} years of deposits
                  </div>
                </div>

                {/* Interest Earned */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Interest Earned</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.estimatedReturns).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext green">
                    +{growthPercentage}% Growth (Tax-Free)
                  </div>
                </div>

                {/* Maturity Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Maturity Amount</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.futureValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At age {result.maturityAge} with {annualInterestRate}% interest
                  </div>
                </div>

                {/* Inflation Adjusted Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Inflation Adjusted Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.inflationAdjustedValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Real value in today's terms
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">SSY Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Your Investment</span>
                      <span className="breakdown-percentage">
                        {((Number(result.totalInvestment) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(Number(result.totalInvestment) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest Earned</span>
                      <span className="breakdown-percentage green">
                        {((Number(result.estimatedReturns) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(Number(result.estimatedReturns) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Sukanya Samriddhi Yojana Calculator Work?</h3>
              <p>
                The SSY calculator estimates the maturity amount for your girl child's account by 
                calculating compound interest on yearly deposits made for 15 years. The account 
                matures 21 years from opening, earning interest for the full period. SSY offers 
                the highest interest rate among government small savings schemes with complete EEE 
                (Exempt-Exempt-Exempt) tax benefits, making it ideal for securing your daughter's future.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              M = P × [((1 + r)^n - 1) / r] × (1 + r)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">M</div>
                <div className="formula-param-value">Maturity Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Yearly Deposit</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Annual Interest Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Total Years to Maturity</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is Sukanya Samriddhi Yojana (SSY)?</h5>
              <p>
                Sukanya Samriddhi Yojana is a government-backed savings scheme exclusively for the 
                girl child. It can be opened for a girl child below 10 years of age. You need to 
                deposit money for 15 years, and the account matures after 21 years from opening. 
                SSY offers the highest interest rate (currently 8.2%) among all government small 
                savings schemes with complete tax exemption on deposits, interest, and maturity amount.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the eligibility criteria for SSY?</h5>
              <p>
                An SSY account can be opened for a girl child from birth until she completes 10 years 
                of age. Parents or legal guardians can open the account in the name of the girl child. 
                Only one account per girl child is allowed, and a maximum of two accounts can be 
                opened by parents (for two girl children). In case of twins or triplets, proof is 
                required to open a third account.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the deposit limits in SSY?</h5>
              <p>
                The minimum annual deposit required is ₹250, and the maximum is ₹1.5 lakh per 
                financial year. You can make deposits in lump sum or in installments (maximum 12 
                times per year). Deposits must be made for 15 years from the date of account opening. 
                After 15 years, no deposits are required, but the account continues to earn interest 
                until maturity (21 years from opening).
              </p>
            </div>

            <div className="faq-item">
              <h5>What happens if I miss depositing in a year?</h5>
              <p>
                If you fail to deposit the minimum ₹250 in a financial year, the account becomes 
                inactive. You can revive it by paying ₹50 as penalty for each year of default along 
                with the minimum deposit of ₹250 per year. The account can be revived at any time 
                before maturity. However, inactive accounts continue to earn interest on the existing 
                balance.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I withdraw money before maturity?</h5>
              <p>
                Partial withdrawal of up to 50% of the balance is allowed after the girl child turns 
                18 years for higher education or marriage purposes. Complete premature closure is 
                permitted in extreme cases like death of the girl child or in cases of extreme 
                compassionate grounds. The girl child can operate the account independently after 
                turning 18 years and can also close the account after turning 18.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the tax benefits of SSY?</h5>
              <p>
                SSY enjoys EEE (Exempt-Exempt-Exempt) tax status - deposits up to ₹1.5 lakh qualify 
                for deduction under Section 80C, interest earned is completely tax-free, and the 
                maturity amount is also exempt from tax. This makes SSY more tax-efficient than PPF 
                and offers higher returns. SSY is one of the best investment options for securing 
                your daughter's education and marriage expenses.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SukanyaSamriddhiCalculator;